import React from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  CheckCircle,
  BookOpen,
  GraduationCap,
  Coins,
  CreditCard,
  Receipt,
} from 'lucide-react';
import { cardTheme } from '../../styles/cardTheme';
import { formatCoins } from '../../utils/walletLabels';
import {
  PaymentSuccessDetails,
  formatEgyptianPaymentDate,
  getPaymentMethodLabel,
} from '../../types/paymentSuccess';
interface PaymentSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  details: PaymentSuccessDetails | null;
}

const PaymentSuccessModal: React.FC<PaymentSuccessModalProps> = ({
  isOpen,
  onClose,
  details,
}) => {
  if (!isOpen || !details) return null;

  const paidAtLabel = details.paidAt
    ? formatEgyptianPaymentDate(new Date(details.paidAt))
    : formatEgyptianPaymentDate();
  const isWallet = details.paymentMethod === 'wallet';
  const amountLabel = isWallet
    ? `${formatCoins(details.coinsSpent ?? details.amount)} عملة`
    : `${formatCoins(details.amount)} جنيه مصري`;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4"
      dir="rtl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-success-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label="إغلاق"
      />

      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-green-100">
        <div className="p-4 sm:p-6 border-b border-green-100 bg-gradient-to-br from-green-50 to-gold-50/40">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-green-700" />
              </div>
              <div className="min-w-0">
                <h2 id="payment-success-title" className={`text-lg sm:text-xl font-bold ${cardTheme.title}`}>
                  تم الدفع بنجاح
                </h2>
                <p className={`text-sm mt-1 ${cardTheme.subtitle}`}>
                  تم تأكيد عمليتك ويمكنك البدء في التعلم الآن
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 p-2 rounded-lg hover:bg-white/80 min-h-11 min-w-11 flex items-center justify-center"
              aria-label="إغلاق"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div className={`${cardTheme.shell} p-4 space-y-3`}>
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="w-4 h-4 text-gold-600" />
              <h3 className={`text-sm font-bold ${cardTheme.title}`}>بيانات العملية — مصر</h3>
            </div>

            {details.courseName && (
              <div className="flex justify-between gap-3 text-sm">
                <span className="text-gray-600">الكورس</span>
                <span className="font-semibold text-gray-900 text-left">{details.courseName}</span>
              </div>
            )}

            <div className="flex justify-between gap-3 text-sm">
              <span className="text-gray-600">المبلغ المدفوع</span>
              <span className="font-bold text-green-800">{amountLabel}</span>
            </div>

            <div className="flex justify-between gap-3 text-sm">
              <span className="text-gray-600">العملة</span>
              <span className="font-medium text-gray-900">
                {details.currency ?? 'EGP'} — جنيه مصري
              </span>
            </div>

            <div className="flex justify-between gap-3 text-sm">
              <span className="text-gray-600">طريقة الدفع</span>
              <span className="font-medium text-gray-900 flex items-center gap-1.5">
                {isWallet ? (
                  <Coins className="w-4 h-4 text-gold-600" />
                ) : (
                  <CreditCard className="w-4 h-4 text-green-700" />
                )}
                {getPaymentMethodLabel(details.paymentMethod)}
              </span>
            </div>

            {details.promoCode && (
              <div className="flex justify-between gap-3 text-sm">
                <span className="text-gray-600">كود الخصم</span>
                <span className="font-medium text-green-700">{details.promoCode.toUpperCase()}</span>
              </div>
            )}

            {details.discountAmount != null && details.discountAmount > 0 && (
              <div className="flex justify-between gap-3 text-sm text-green-700">
                <span>قيمة الخصم</span>
                <span className="font-semibold">-{formatCoins(details.discountAmount)} جنيه</span>
              </div>
            )}

            {details.orderId && (
              <div className="flex justify-between gap-3 text-sm">
                <span className="text-gray-600">رقم الطلب</span>
                <span className="font-mono text-xs text-gray-800 break-all text-left">{details.orderId}</span>
              </div>
            )}

            {details.transactionId && (
              <div className="flex justify-between gap-3 text-sm">
                <span className="text-gray-600">رقم العملية</span>
                <span className="font-mono text-xs text-gray-800 break-all text-left">
                  {details.transactionId}
                </span>
              </div>
            )}

            {isWallet && details.balanceAfter != null && (
              <div className="flex justify-between gap-3 text-sm">
                <span className="text-gray-600">رصيد المحفظة بعد الدفع</span>
                <span className="font-bold text-gold-700">
                  {formatCoins(details.balanceAfter)} عملة
                </span>
              </div>
            )}

            <div className="h-px bg-gold-300/50" />

            <div className="flex justify-between gap-3 text-sm">
              <span className="text-gray-600">التاريخ والوقت</span>
              <span className="font-medium text-gray-900 text-left">{paidAtLabel}</span>
            </div>

            <p className="text-xs text-gray-500 leading-relaxed pt-1">
              تم تسجيل العملية وفق أنظمة الدفع المعتمدة في جمهورية مصر العربية. احتفظ برقم الطلب
              للمراجعة عند الحاجة.
            </p>
          </div>

          <div className="space-y-2">
            <Link
              to={`/course-player/${details.courseId}`}
              onClick={onClose}
              className={`w-full min-h-11 py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 ${cardTheme.btnPrimary}`}
            >
              <BookOpen className="w-5 h-5" />
              ابدأ التعلم الآن
            </Link>
            <Link
              to="/student-portal"
              onClick={onClose}
              className="w-full min-h-11 py-3 px-4 rounded-xl font-semibold border-2 border-green-200 text-green-800 hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
            >
              <GraduationCap className="w-5 h-5" />
              عرض كورساتي
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessModal;
